import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useSWR from "swr";
import type { AxiosError } from "axios";
import Axios from "axios";
import {
  ButtonWithConfirmDialog,
  DeleteButton,
  EditButton,
  PrimaryButtonWithPlusIcon,
} from "../../../../../components/Buttons/Buttons";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { Row } from "../../../../../components/Layout/Layout";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { H3 } from "../../../../../components/Typography/Typography";
import { BorderCard } from "../../../../../layout/portalPageLayout";
import type {
  StorefrontFilterSetting,
  WithPagination,
} from "../../../../../types/types";
import { TablePlaceholder, useStoreState } from "../../../../../util/util";
import { SampleAndQuoteRequestSettings } from "./SampleAndQuoteRequestSettings";
import { Table } from "../../../../../components/Table/Table";
import type { PortfolioControlsSchema } from "../../../../../types/types.PIM";
import { screenSize } from "../../../../../theme";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import { PortfolioControlForm } from "./PortfolioControlForm";
import ReactTooltip from "react-tooltip";
import { endpoints } from "../../../../../endpoints";
import { EnterpriseSearchSuggestionsSettings } from "./EnterpriseSearchSuggestionsSettings";
import { SearchBar } from "../../../../../components/SearchBar/SearchBar";
import { Flex1 } from "../../../../../layout/FormLayout";
import { useDebounce } from "../../../../../util/hooks";
import { StringParam, useQueryParams } from "use-query-params";
import { ScrollTableWrapper } from "../../../../SharedPages/ProductDetailPage/Assets/DocumentsOrDigitalMediaTable/DocumentsOrDigitalMediaTable";
import { TableContainer } from "../../../../../components/Table/TableWithBulkAction";

export const SearchFilterCaption = styled.p`
  margin: 8px 0 30px;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.primaryTextColor};
`;

const IconGroup = styled.div`
  display: flex;
  gap: 20px;
  @media ${screenSize.small} {
    gap: 10px;
  }
  align-items: center;
`;

export const SettingsHeader = styled.div`
  padding-bottom: 12px;
  margin-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.secondaryBorder};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SettingsDescription = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.grayText};
`;

export const TableSearchWrapper = styled(Flex1)`
  border-left: ${({ theme }) => `4px solid ${theme.secondaryBorder}`};
  border-top: ${({ theme }) => `1px solid ${theme.secondaryBorder}`};
  border-right: ${({ theme }) => `1px solid ${theme.secondaryBorder}`};
  padding: 12px 16px;
`;

/**
 * Only the user modifiable properties of `StorefrontFilterSettings`, this type
 * is used as state in this component and `SearchAndFilterSettingsItem`
 */
export type ModifiableFilterSettings = Pick<
  StorefrontFilterSetting,
  "is_active" | "is_searchable" | "is_visible"
>;

/**
 * Requirements Stage 2:
 * This is used in the storefront settings page.
 * It is a new tab with title; "Search & Filter".
 * The seller admin can select tags and classifications that will appear as filters on the portfolio.
 * Under the “Search & Filter“ tab in the settings tab, there is a list of all tags (industry, market segment, application, function, value proposition) and classifications (product group 1, 2 and 3).
 * We also have a custom filter & search on a portfolio called Producers.
 * For each of these 9 filters, seller admin should be able to:
 * 1. Toggle ON/OFF the Filter & Search Criteria.
 * If filter option is ON, the filter will appear on portfolio. If unchecked, the filter will NOT appear on portfolio.
 * If search option is ON, the buyer shall be able to perform search on portfolio by the respective filter.
 * If search option is OFF, the respective filter cannot be used as a search criteria on the portfolio.
 *
 * @returns
 */

export const PortfolioControlsTab = () => {
  const [tableData, setTableData] = useState<PortfolioControlsSchema[]>([]);
  const [showSlideOut, setSlideOut] = useState(false);
  const [editingPortfolioControl, setEditingPortfolioControl] =
    useState<PortfolioControlsSchema>();
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useContext(Notifications);
  const {
    storefront_id,
    storefront_metadata: {
      default_language,
      edition,
      enable_vertex_ai_search,
      max_number_of_filter_settings,
    },
  } = useStoreState();
  const [query, setQuery] = useQueryParams({ q: StringParam });
  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const perPage = 100;

  const {
    data: portfolioControlsData,
    error: portfolioControlsError,
    mutate: mutateControls,
  } = useSWR<WithPagination<{ data: PortfolioControlsSchema[] }>>(
    [
      `v2/storefronts/${storefront_id}/portfolio-controls`,
      useMemo(
        () => ({
          params: {
            q: debouncedSearchQuery,
            limit: perPage,
          },
        }),
        [debouncedSearchQuery, perPage]
      ),
    ],
    {
      onSuccess: ({ data: controlsData }) => {
        setTableData(controlsData);
      },
      revalidateOnFocus: true,
    }
  );

  const { data: productSelectors } = useSWR<
    { selectors: { name: string; order: string }[] },
    AxiosError
  >(endpoints.v2_storefronts_id_product_selectors(storefront_id));

  const tableColumns = useMemo(() => {
    const deletePortfolioControl = async (
      portfolioControl: PortfolioControlsSchema
    ) => {
      try {
        await Axios.delete(
          `v2/storefronts/${storefront_id}/portfolio-controls/${portfolioControl.id}`
        );
        mutateControls();
        notifySuccess(t("Attribute deleted successfully"));
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage
            ? errorMessage
            : t("There was an error deleting Attribute"),
          {
            error,
          }
        );
      }
    };

    const editPortfolioControl = (
      portfolioControl: PortfolioControlsSchema
    ) => {
      setEditingPortfolioControl(portfolioControl);
      setSlideOut(true);
    };

    return [
      {
        header: t("Attribute Name"),
        cell: ({
          row: { original },
        }: {
          row: {
            original: PortfolioControlsSchema;
          };
        }) => {
          return <>{original.attribute?.name || "--"}</>;
        },
      },
      {
        header: t(`Display Name (${default_language.toUpperCase()})`),
        cell: ({
          row: { original },
        }: {
          row: {
            original: PortfolioControlsSchema;
          };
        }) => {
          return (
            <>{original.display_name ? t([original.display_name]) : "--"}</>
          );
        },
      },
      {
        header: t("Search"),
        accessorKey: "",
        cell: ({
          row: { original },
        }: {
          row: {
            original: PortfolioControlsSchema;
          };
        }) => {
          return (
            <ToggleSwitchV2
              name={`${original.id}_is_searchable`}
              label=""
              defaultChecked={original.is_searchable}
              disabled={true}
            />
          );
        },
      },
      {
        header: t("Filter"),
        accessorKey: "",
        cell: ({
          row: { original },
        }: {
          row: {
            original: PortfolioControlsSchema;
          };
        }) => {
          return original?.attribute?.is_filterable ? (
            <ToggleSwitchV2
              name={`${original.id}_is_filterable`}
              label=""
              defaultChecked={original.is_filterable}
              disabled={true}
            />
          ) : (
            <></>
          );
        },
      },
      {
        header: "",
        accessorKey: " ",
        overflow: "visible",
        cell: ({
          row: { original },
        }: {
          row: {
            original: PortfolioControlsSchema;
          };
        }) => {
          return (
            <>
              <IconGroup style={{ justifyContent: "flex-end" }}>
                <EditButton onClick={() => editPortfolioControl(original)} />
                <div
                  data-for="delete-portfolio-control-item"
                  data-tip={
                    productSelectors?.selectors
                      ?.map((selector) => selector.name)
                      .indexOf(
                        original.attribute.new_object_type.toLocaleLowerCase()
                      ) !== -1
                      ? t(
                          "You cannot delete this attribute as it's used as a product selector"
                        )
                      : ""
                  }
                >
                  <ButtonWithConfirmDialog
                    testid={"delete-portfolio-control-item"}
                    disabled={
                      productSelectors?.selectors
                        ?.map((selector) => selector.name)
                        .indexOf(
                          original.attribute.new_object_type.toLocaleLowerCase()
                        ) !== -1
                    }
                    Button={DeleteButton}
                    handleConfirm={() => deletePortfolioControl(original)}
                    confirmMessage={t(
                      "Are you sure you want to remove this item?"
                    )}
                  />
                </div>
                <ReactTooltip id="delete-portfolio-control-item" place="top" />
              </IconGroup>
            </>
          );
        },
      },
    ];
  }, [
    t,
    default_language,
    notifyError,
    productSelectors,
    notifySuccess,
    storefront_id,
    mutateControls,
  ]);

  const closeSlideOut = () => {
    mutateControls();
    setSlideOut(false);
    setEditingPortfolioControl(undefined);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setQuery({ q: undefined });
  };

  useEffect(() => {
    setQuery({ q: !!debouncedSearchQuery ? debouncedSearchQuery : undefined });
  }, [setQuery, debouncedSearchQuery]);

  if (portfolioControlsError) {
    return <ErrorPlaceholder message={t("Failed to fetch filter settings")} />;
  } else {
    return (
      <>
        {edition !== "pim" && <SampleAndQuoteRequestSettings />}
        {enable_vertex_ai_search && <EnterpriseSearchSuggestionsSettings />}
        <BorderCard>
          <SettingsHeader>
            <TextContainer>
              <H3 style={{ margin: 0 }}>{t("Search & Filter Controls")}</H3>
              <SettingsDescription>
                {t(
                  "Manage attributes to control search and filtering on your portfolio, ensuring better discoverability and streamlined navigation."
                )}
              </SettingsDescription>
            </TextContainer>
          </SettingsHeader>
          <TableSearchWrapper>
            <Row>
              <SearchBar
                query={searchQuery}
                placeHolder={t("Search by attribute")}
                handleChange={handleSearch}
                handleClearInput={handleClearSearch}
                maxWidth="450px"
              />
              {portfolioControlsData && (
                <>
                  {portfolioControlsData?.pagination?.total >=
                  max_number_of_filter_settings ? (
                    <div
                      data-for="max-tooltip"
                      data-tip={t(
                        "You’ve reached the maximum limit of attributes."
                      )}
                      style={{ display: "inline-block" }}
                    >
                      <PrimaryButtonWithPlusIcon
                        onClick={() => setSlideOut(true)}
                        disabled
                        style={{ pointerEvents: "none", padding: "8px 10px" }}
                      >
                        {t("Add")}
                      </PrimaryButtonWithPlusIcon>
                      <ReactTooltip id="max-tooltip" place="right" />
                    </div>
                  ) : (
                    <PrimaryButtonWithPlusIcon
                      onClick={() => setSlideOut(true)}
                      style={{ padding: "8px 10px" }}
                    >
                      {t("Add")}
                    </PrimaryButtonWithPlusIcon>
                  )}
                </>
              )}
            </Row>
          </TableSearchWrapper>
          <TableContainer>
            <ScrollTableWrapper maxHeight="697px">
              <Table
                columns={tableColumns}
                data={tableData}
                isLoading={!portfolioControlsError && !portfolioControlsData}
                error={undefined}
                Placeholder={
                  <TablePlaceholder message={t("No items to show.")} />
                }
              />
            </ScrollTableWrapper>
          </TableContainer>
          <SlideOut
            closeFlyout={closeSlideOut}
            show={showSlideOut}
            top="1.5rem"
            right="1.5rem"
          >
            <PortfolioControlForm
              portfolioControl={editingPortfolioControl}
              handleFormSubmit={closeSlideOut}
              portfolioControlsData={portfolioControlsData?.data}
              productSelectors={productSelectors?.selectors}
            />
          </SlideOut>
        </BorderCard>
      </>
    );
  }
};
